/** @jsxImportSource theme-ui @jsxRuntime classic */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  animated,
  Controller,
  SpringValue,
  useSpring,
} from '@react-spring/web';
import { Input as ThemeUiInput } from 'theme-ui';
import {
  Label,
  AnimationState,
  useAnimationStateSelector,
  usePageTransitionActions,
  usePageTransitionDispatch,
  useThemeUI,
  Box,
} from '@tishman/components';

import type { ComponentProps } from 'react';

export function SearchInput({
  onChange,
  defaultValue,
  ...props
}: ComponentProps<typeof ThemeUiInput>): JSX.Element {
  const actions = usePageTransitionActions();
  const dispatch = usePageTransitionDispatch();
  const themeUI = useThemeUI();

  const [hasInput, setHasInput] = useState(Boolean(defaultValue));

  const ctrlIds = useRef(new Set<number>());

  const [transition, ref] = useSpring((i: SpringValue, ctrl: Controller) => {
    ctrlIds.current.add(ctrl.id);

    return {
      scaleX: 0,
    };
  });

  useEffect(() => {
    const ctrlIds_ = ctrlIds.current;

    ctrlIds_.forEach((animationId) => {
      dispatch(
        actions.add({
          animationId,
          animationState: AnimationState.SHOULD_ENTER,
        }),
      );
    });

    return () => {
      ctrlIds_.forEach((animationId) => {
        dispatch(actions.remove(animationId));
      });
    };
  }, [actions, dispatch]);

  const animationState = useAnimationStateSelector([...ctrlIds.current]);

  useLayoutEffect(() => {
    switch (animationState) {
      case AnimationState.SHOULD_ENTER: {
        ref.start({
          delay: 300,
          onStart: (_, ctrl) => {
            dispatch(
              actions.set({
                animationId: ctrl.id,
                animationState: AnimationState.IS_ENTERING,
              }),
            );
          },
          onRest: (_, ctrl) => {
            dispatch(
              actions.set({
                animationId: ctrl.id,
                animationState: AnimationState.DID_ENTER,
              }),
            );
          },
          scaleX: 1,
        });
        break;
      }
      case AnimationState.SHOULD_LEAVE: {
        ref.start({
          onStart: (_, ctrl) => {
            dispatch(
              actions.set({
                animationId: ctrl.id,
                animationState: AnimationState.IS_LEAVING,
              }),
            );
          },
          onRest: (_, ctrl) => {
            dispatch(
              actions.set({
                animationId: ctrl.id,
                animationState: AnimationState.DID_LEAVE,
              }),
            );
          },
          scaleX: 0,
        });
        break;
      }
    }
  }, [actions, animationState, dispatch, ref]);

  return (
    <Label
      color="muted"
      sx={{
        position: 'relative',
        fontWeight: 'regular',
        fontSize: [4, null, 5, 6],
        lineHeight: 1,
        maxWidth: 990,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 0,
          width: '100%',
          paddingTop: themeUI.theme.space[2],
        }}
      >
        {/*
          Note: We have two placeholder labels on purpose: the 'real' label,
          which should be visible on large screens, and the small label,
          which shows a different label on small screens.
          Screen readers should only see the real label.
        */}
        <Box as="span" sx={{ display: hasInput ? 'none' : ['none', 'block'] }}>
          Search by keyword, event, etc.
        </Box>
        <Box as="span" sx={{ display: hasInput ? 'none' : ['block', 'none'] }}>
          Search
        </Box>
        <span
          // a11y: this is a visually hidden label
          style={{
            position: 'absolute',
            height: '1px',
            width: '1px',
            overflow: 'hidden',
            clip: 'rect(1px, 1px, 1px, 1px)',
            whiteSpace: 'nowrap',
          }}
        >
          Navigate forward for suggested search terms
        </span>
      </span>
      <ThemeUiInput
        {...props}
        defaultValue={defaultValue}
        onChange={(event) => {
          setHasInput(Boolean(event.target.value));
          onChange?.(event);
        }}
        sx={{
          py: 2,
          fontWeight: 'medium',
          fontSize: [4, null, 5, 6],
          border: 'none',
          outline: 'none',
          lineHeight: 1,
        }}
      />
      <animated.div
        style={{
          ...transition,
          width: '100%',
          height: '1px',
          backgroundColor: themeUI.theme.colors.text,
          transformOrigin: 'center left',
        }}
      />
    </Label>
  );
}
